<script>
  import { onMount, createEventDispatcher } from "svelte";

  import { formatEther } from "viem";
  import { getPublicClient, writeContract } from "@wagmi/core";

  import { zeroAddress, getContractType, getABI, getContractValue } from "../utils.js";
  import { chainExplorer } from "../stores.js";

  /** @type {number} */
  export let chain;
  /** @type {string} */
  export let address;

  let type;
  let connected = false;
  let holder;
  let paymentFreq;
  let contractAddress;
  let contractName;
  let contractSymbol;
  let fungible;
  let mintRole;
  let allowanceNeeded;
  let allowing = false;
  let txHash;

  let dialog;

  const initCollectionsVending = async () => {
    type = 'CollectionsVending';
    holder = await getContractValue(chain, address, 'holder');
    const [ name, tokenId] = await Promise.all([
      getContractValue(chain, contractAddress, 'name'),
      getContractValue(chain, address, 'tokenId'),
    ]);
    contractName = name;

    if (holder === zeroAddress) {
      connected = await getContractValue(chain, contractAddress, 'isApprovedForAll', [ holder, address ]);
    } else {
      mintRole = await getContractValue(chain, contractAddress, 'MINTER_ROLE');
      connected = await getContractValue(chain, contractAddress, 'hasRole', [ mintRole, address ]);
    }
  }

  const initCollectionFaucet = async tokenId => {
    type = 'CollectionFaucet';
    contractName = await getContractValue(chain, contractAddress, 'name');
    if (holder === zeroAddress) {
      mintRole = await getContractValue(chain, contractAddress, 'MINTER_ROLE');
      connected = await getContractValue(chain, contractAddress, 'hasRole', [ mintRole, address ]);
    } else {
      connected = await getContractValue(chain, contractAddress, 'isApprovedForAll', [ holder, address ]);
    }
  }

  const initCoinVending = async () => {
    type = 'CoinVending';
    const [ hold, amount, price ] = await Promise.all([
      getContractValue(chain, address, 'holder'),
      getContractValue(chain, address, 'amount'),
      getContractValue(chain, address, 'price')
    ]);
    holder = hold;
    allowanceNeeded = amount * price;
    console.log(allowanceNeeded, formatEther(allowanceNeeded))

    const [ name, symbol, allowance ] = await Promise.all([
      getContractValue(chain, contractAddress, 'name'),
      getContractValue(chain, contractAddress, 'symbol'),
      getContractValue(chain, contractAddress, 'allowance', [ holder, address ])
    ]);
    contractName = name;
    contractSymbol = symbol;
    connected = allowance > BigInt(0);
  }

  const initCoinFaucet = async () => {
    type = 'CoinFaucet';
    const [ payment, fire] = await Promise.all([
      getContractValue(chain, address, 'payment'),
      getContractValue(chain, address, 'fire')
    ]);
    if (payment[2]) {
      allowanceNeeded = payment[2] * fire[1];
    } else {
      fireFee = fire[1];
    }
    const [ name, symbol, allowance ] = await Promise.all([
      getContractValue(chain, contractAddress, 'name'),
      getContractValue(chain, contractAddress, 'symbol'),
      getContractValue(chain, contractAddress, 'allowance', [ holder, address ])
    ]);
    contractName = name;
    contractSymbol = symbol;
    connected = allowance > BigInt(0);
  }

  const init = async () => {
    const contractType = await getContractType(address, chain);
    if (contractType === 'Vending') {
      contractAddress = await getContractValue(chain, address, 'contractAddress');
      const cType = await getContractType(contractAddress, chain);
      if (cType === 'Collection2') {
        await initCollectionsVending();
      } else {
        await initCoinVending();
      }
    } else if (contractType === 'Faucet') {
      const [contractData, frequency] = await Promise.all([
        getContractValue(chain, address, 'contractData'),
        getContractValue(chain, address, 'frequency')
      ]);
      contractAddress = contractData[0];
      holder = contractData[2];
      paymentFreq = frequency;
      const cType = await getContractType(contractAddress, chain);
      if (cType === 'Collection2') {
        await initCollectionFaucet(contractData[1]);
      } else {
        await initCoinFaucet();
      }
    }
  }

  const toggle = async () => {
    allowing = true;
    if (type.startsWith('Coin')) {
      var functionName = 'approve';
      var args = [ address, connected ? 0 : allowanceNeeded ];
    } else {
      functionName = 'setApprovalForAll';
      args = [ address, !connected ];
    }
    try {
      const { hash } = await writeContract({
        chainId: chain, address: contractAddress, abi: await getABI(contractAddress, chain),
        functionName, args
      });
      txHash = hash;
    } catch (err) {
      if (err.shortMessage === 'User rejected the request.') {
        dispatch('user-cancel');
        new CustomEvent('user-cancel');
        allowing = undefined;
      } else {
        console.log(err)
        dispatch('error', err);
        new CustomEvent('error', { detail: err });
      }
      dialog.close();
      return;
    }

    dispatch('hashed', txHash);
    new CustomEvent('hashed', { detail: txHash });
    const receipt = await getPublicClient().waitForTransactionReceipt({ hash: txHash });
    dialog.close();
    connected = !connected;
    dispatch('toggled', receipt)
    new CustomEvent('toggled', { detail: receipt });
    txHash = undefined;
    allowing = undefined;
  }

  const toggleRole = async () => {
    allowing = true;

    const functionName = connected ? 'revokeRole' : 'grantRole';
    const args = [ mintRole, address ]

    try {
      const { hash } = await writeContract({
        chainId: chain, address: contractAddress, abi: await getABI(contractAddress, chain),
        functionName, args
      });
      txHash = hash;
    } catch (err) {
      if (err.shortMessage === 'User rejected the request.') {
        dispatch('user-cancel');
        new CustomEvent('user-cancel');
        allowing = undefined;
      } else {
        console.log(err)
        dispatch('error', err);
        new CustomEvent('error', { detail: err });
      }
      dialog.close();
      return;
    }

    dispatch('hashed', txHash);
    new CustomEvent('hashed', { detail: txHash });
    const receipt = await getPublicClient().waitForTransactionReceipt({ hash: txHash });
    dialog.close();
    connected = !connected;
    dispatch('toggled', receipt)
    new CustomEvent('toggled', { detail: receipt });
    txHash = undefined;
    allowing = undefined;
  }

  onMount(init);

  const dispatch = createEventDispatcher();
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<span class={ $$restProps.class } class:lt-allowed={ true } class:connected={ connected } onclick="confirm{ chain }{ address }toggle.showModal()">
  <slot connected={ connected }>&#9211;</slot>
</span>
<dialog id="confirm{ chain }{ address }toggle" class="lt-allowed-modal" bind:this={ dialog }>
  <form method="dialog">
    {#if txHash}
      <h3>The transaction has been sended to the network</h3>
      <p>Waiting to be confirmed</p>
      <div>
        <a href="{ $chainExplorer.url }/tx/{ txHash }" target="_blank" class="lt-allowed-tx">View tx</a>
      </div>
    {:else if [ 'CoinVending', 'CoinFaucet' ].includes(type)}
      {#if connected}
        <h3>Remove allowance</h3>
        <p>
          By clicking remove, this contract will be not allowed to transfer tokens on behalf of the holder
        </p>
      {:else}
        <h3>Add allowance</h3>
        <p>
          By clicking allow, this contract will be allowed to transfer { formatEther(allowanceNeeded || BigInt(0)) }<span class="symbol">{ contractSymbol }</span> from { contractName }
        </p>
      {/if}
      <div>
        <button type="button" class="toggle" on:click={ toggle }>
          {#if allowing}
            <span class="loading loading-spinner"></span>
          {/if}
          { connected ? 'Remove' : 'Allow' }
        </button>
        <button class="cancel">Cancel</button>
      </div>
    {:else if fungible}
      {#if connected}
        <h3>Remove approval</h3>
        <p>
          By clicking remove, this contract will not be approved to transfer tokens on behalf of the holder
        </p>
      {:else}
        <h3>Approve</h3>
        <p>
          By clicking allow, this contract will be allowed to transfer tokens from { contractName }
        </p>
      {/if}
      <div>
        <button type="button" class="toggle" on:click={ toggle }>
          {#if allowing}
            <span class="loading loading-spinner"></span>
          {/if}
          { connected ? 'Remove' : 'Approve' }
        </button>
        <button class="cancel">Cancel</button>
      </div>
    {:else}
      {#if connected}
        <h3>Revoke role</h3>
        <p>
          By clicking revoke, this contract will not be allowed to mint any token on the collection
        </p>
      {:else}
        <h3>Grant role</h3>
        <p>
          By clicking grant, this contract will be allowed to mint tokens on the collection
        </p>
      {/if}
      <div>
        <button type="button" class="toggle" on:click={ toggleRole }>
          {#if allowing}
            <span class="loading loading-spinner"></span>
          {/if}
          { connected ? 'Revoke' : 'Grant' }
        </button>
        <button class="cancel">Cancel</button>
      </div>
    {/if}
  </form>
</dialog>